import { gql, useQuery } from '@apollo/client';
import _ from 'lodash';
import React, { useMemo } from 'react';

import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { useSelectedClients } from 'src/hooks/useSelectedClients';
import * as ApolloTypes from 'src/lib/graphqlTypes/types';

// exported for storybook
export const clientFilterQuery = gql`
  query UI__ClientListQuery(
    $serviceId: ID!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
    $filter: ServiceQueryStatsFilter
  ) {
    service(id: $serviceId) {
      id
      stats: statsWindow(from: $timeFrom, to: $timeTo) {
        queryStats(filter: $filter) {
          group: groupBy {
            clientName
            clientVersion
          }
        }
      }
    }
  }
`;

export interface ClientFilterInfo {
  selectedClients?: Record<string, '*' | Array<string>>;
  allClients?: Record<string, Array<string>>;
  clientFilter?:
    | Array<{
        clientName: string;
        in?: { clientVersion: Array<string> };
      }>
    | Array<{}>;
  loadingClients: boolean;
}

interface Props {
  children: (clientInfo: ClientFilterInfo) => React.ReactElement | null;
  from: Date | string;
  to: Date | string;
}

const getAllClients = (
  queryStats:
    | ApolloTypes.UI__ClientListQuery_service_stats_queryStats[]
    | undefined,
) => {
  if (!queryStats) return;

  // XXX(Chang): this code is on a hot path, avoid creating a new object/scope for each loop
  const clientVersionsByClientName: Record<string, Array<string>> = {};
  for (const { group } of queryStats) {
    const clientName = group.clientName || '';
    const clientVersions = (clientVersionsByClientName[clientName] =
      clientVersionsByClientName[clientName] || []);
    clientVersions.push(group.clientVersion || '');
  }

  return clientVersionsByClientName;
};

// XXX: we're memoizing outside react because this component is actually used in multiple places
// we can move memoizatino back into react if we move this data into context and only have one place to access this
const memoizedGetAllClients = _.memoize(getAllClients);

export const ClientProvider = ({ children, from, to }: Props) => {
  const graphRef = useGraphRef();
  const [selectedClients] = useSelectedClients();
  const clientFilter = useMemo(() => {
    if (selectedClients) {
      const entries = Object.entries(selectedClients);
      // if we have no entries then don't create a filter, meaning that we should
      // skip the query
      if (entries.length > 0) {
        return entries.map(([name, versions]) => ({
          clientName: name,
          ...(Array.isArray(versions) && {
            in: {
              clientVersion: versions,
            },
          }),
        }));
      }
    } else {
      // default to retreive all clients
      return [];
    }
  }, [selectedClients]);

  const { data, loading, error } = useQuery<
    ApolloTypes.UI__ClientListQuery,
    ApolloTypes.UI__ClientListQueryVariables
  >(clientFilterQuery, {
    variables: {
      serviceId: graphRef?.graphId || '',
      timeFrom: from instanceof Date ? from.toISOString() : from,
      timeTo: to instanceof Date ? to.toISOString() : to,
      filter: {
        in: {
          schemaTag:
            !graphRef?.graphVariant || graphRef.graphVariant === 'current'
              ? ['', 'current']
              : [graphRef.graphVariant],
        },
      },
    },
  });

  const queryStats = data?.service?.stats?.queryStats;

  const allClients = memoizedGetAllClients(queryStats);

  if (error) {
    console.log(error); // eslint-disable-line no-console
  }

  const childProps = {
    selectedClients,
    allClients,
    clientFilter,
    loadingClients: loading,
  };

  return children(childProps);
};
